<template>
  <div class="agg-sections">

    <!-- Vue d'ensemble -->
    <div class="reporting-section">
      <div class="section-header">
        <i class="material-icons section-icon">dashboard</i>
        <h2>{{ $t('dash.reporting.overview') }}</h2>
        <span class="agg-badge">{{ networksLabel }}</span>
        <span v-if="prevLoading" class="prev-loading-badge">
          <span class="prev-loading-dot"></span>{{ $t('dash.reporting.prev_loading') }}
        </span>
      </div>
      <div class="metric-row">
        <MetricCard :value="totals.page_fans"        :previous-value="prev.page_fans"        :label="$t('dash.reporting.followers')"    icon="group" />
        <MetricCard :value="totals.nb_posts"         :previous-value="prev.nb_posts"         :label="$t('dash.reporting.publications')" icon="edit" />
        <MetricCard :value="totals.totalImpressions" :previous-value="prev.totalImpressions" :label="$t('dash.reporting.impression')"   icon="visibility" />
        <MetricCard :value="totals.totalEngagement"  :previous-value="prev.totalEngagement"  :label="$t('dash.reporting.interactions')" icon="fa-solid fa-hand-point-up" />
        <MetricCard :value="totals.totalReactions"   :previous-value="prev.totalReactions"   :label="$t('dash.reporting.reaction')"     icon="thumb_up" />
        <MetricCard :value="totals.totalComments"    :previous-value="prev.totalComments"    :label="$t('dash.reporting.comment')"      icon="chat_bubble" />
        <MetricCard :value="totals.totalShares"      :previous-value="prev.totalShares"      :label="$t('dash.reporting.share')"        icon="share" />
        <MetricCard v-if="totals.totalClicks > 0"   :value="totals.totalClicks"   :previous-value="prev.totalClicks"   :label="$t('dash.reporting.clic')"         icon="touch_app" />
        <MetricCard
          v-if="totals.totalImpressions > 0"
          :value="globalEngagementRate"
          :previous-value="prevEngagementRate"
          :label="$t('dash.reporting.engagement_rate')"
          icon="show_chart"
          format="percent"
        />
      </div>
    </div>

    <!-- Audience -->
    <div class="reporting-section" v-if="fansSection.show">
      <div class="section-header">
        <i class="material-icons section-icon">group</i>
        <h2>{{ $t('dash.reporting.section.audience.title') }}</h2>
      </div>
      <div class="metric-row" style="margin-bottom:16px">
        <MetricCard :value="totals.page_fans"      :previous-value="prev.page_fans"      :label="$t('dash.reporting.followers')"       icon="group" />
        <MetricCard v-if="totals.sumFanAdds > 0"    :value="totals.sumFanAdds"    :label="$t('dash.reporting.new_followers')"   icon="person_add" />
        <MetricCard v-if="totals.sumFanRemoves > 0" :value="totals.sumFanRemoves" :label="$t('dash.reporting.nb_fans.lost_fans')" icon="remove_circle" />
        <MetricCard v-if="totals.organicFollowerGain > 0" :value="totals.organicFollowerGain" :label="$t('dash.reporting.organicFollowerGain')" icon="person_add" />
        <MetricCard v-if="totals.paidFollowerGain > 0"    :value="totals.paidFollowerGain"    :label="$t('dash.reporting.paidFollowerGain')"    icon="paid" />
      </div>
      <template v-if="fansSection.chart.data.length">
        <div class="graph-title">{{ $t('dash.reporting.followers') }}</div>
        <Graph id="agg-fans" :data="fansSection.chart.data" :labels="fansSection.chart.labels" height="220px" width="100%" />
      </template>
    </div>

    <!-- Impressions -->
    <div class="reporting-section" v-if="impressionsSection.show">
      <div class="section-header">
        <i class="material-icons section-icon">visibility</i>
        <h2>{{ $t('dash.reporting.section.impressions.title') }}</h2>
      </div>
      <div class="metric-row" style="margin-bottom:16px">
        <MetricCard :value="totals.totalImpressions"      :previous-value="prev.totalImpressions"      :label="$t('dash.reporting.impression')"           icon="visibility" />
        <MetricCard v-if="totals.totalReach > 0"          :value="totals.totalReach"                   :label="$t('dash.label.fb.impressions_reach')"     icon="people" />
        <MetricCard v-if="totals.totalOrganic > 0"        :value="totals.totalOrganic"                 :label="$t('dash.label.fb.impressions_organic')"   icon="eco" />
        <MetricCard v-if="totals.totalPaid > 0"           :value="totals.totalPaid"                    :label="$t('dash.label.fb.impressions_paid')"      icon="monetization_on" />
        <MetricCard v-if="totals.totalViral > 0"          :value="totals.totalViral"                   :label="$t('dash.label.fb.impressions_viral')"     icon="share" />
        <MetricCard v-if="totals.totalUniqueImpressions > 0" :value="totals.totalUniqueImpressions"    :label="$t('dash.reporting.unique_impression')"    icon="visibility_off" />
        <MetricCard v-if="totals.totalPageViews > 0"      :value="totals.totalPageViews"               :label="$t('dash.reporting.page_views')"           icon="web" />
      </div>
      <template v-if="impressionsSection.chart.data.length">
        <div class="graph-title">{{ $t('dash.reporting.impression') }}</div>
        <Graph id="agg-impressions" :data="impressionsSection.chart.data" :labels="impressionsSection.chart.labels" height="220px" width="100%" />
      </template>
    </div>

    <!-- Engagement -->
    <div class="reporting-section" v-if="engagementSection.show">
      <div class="section-header">
        <i class="material-icons section-icon">thumb_up</i>
        <h2>{{ $t('dash.reporting.section.engagements.title') }}</h2>
      </div>
      <div class="metric-row" style="margin-bottom:16px">
        <MetricCard :value="totals.totalEngagement"  :previous-value="prev.totalEngagement"  :label="$t('dash.reporting.interactions')" icon="fa-solid fa-hand-point-up" />
        <MetricCard :value="totals.totalReactions"   :previous-value="prev.totalReactions"   :label="$t('dash.reporting.reaction')"     icon="thumb_up" />
        <MetricCard :value="totals.totalComments"    :previous-value="prev.totalComments"    :label="$t('dash.reporting.comment')"      icon="chat_bubble" />
        <MetricCard :value="totals.totalShares"      :previous-value="prev.totalShares"      :label="$t('dash.reporting.share')"        icon="share" />
        <MetricCard v-if="totals.totalClicks > 0"   :value="totals.totalClicks"  :previous-value="prev.totalClicks"  :label="$t('dash.reporting.clic')"  icon="touch_app" />
        <MetricCard
          v-if="totals.totalImpressions > 0"
          :value="globalEngagementRate"
          :label="$t('dash.reporting.engagement_rate')"
          icon="show_chart"
          format="percent"
        />
      </div>
      <template v-if="engagementSection.chart.data.length">
        <div class="graph-title">{{ $t('dash.reporting.interactions') }}</div>
        <Graph id="agg-engagement" :data="engagementSection.chart.data" :labels="engagementSection.chart.labels" height="220px" width="100%" />
      </template>
    </div>

    <!-- Par réseau (si > 1 réseau) -->
    <div class="reporting-section" v-if="hasMultipleNetworks">
      <div class="section-header">
        <i class="material-icons section-icon">compare_arrows</i>
        <h2>{{ $t('dash.reporting.section.comparison.by_network') }}</h2>
      </div>

      <template v-if="networkImpressionsChart.data.length">
        <div class="graph-title">{{ $t('dash.reporting.impression') }}</div>
        <Graph id="agg-net-impressions" :data="networkImpressionsChart.data" :labels="networkImpressionsChart.labels" height="220px" width="100%" />
      </template>

      <template v-if="networkEngagementChart.data.length">
        <div class="graph-title">{{ $t('dash.reporting.interactions') }}</div>
        <Graph id="agg-net-engagement" :data="networkEngagementChart.data" :labels="networkEngagementChart.labels" height="220px" width="100%" />
      </template>

      <template v-if="networkFansChart.data.length">
        <div class="graph-title">{{ $t('dash.reporting.followers') }}</div>
        <Graph id="agg-net-fans" :data="networkFansChart.data" :labels="networkFansChart.labels" height="220px" width="100%" />
      </template>
    </div>

    <!-- Top 20 posts -->
    <div class="reporting-section" v-if="allPostsForTop.length">
      <div class="section-header">
        <i class="material-icons section-icon">star</i>
        <h2>Top 20 posts</h2>
        <span class="agg-badge">{{ top20Posts.length }} / {{ allPostsForTop.length }} posts</span>
      </div>
      <div class="top20-table">
        <div class="top20-table__head" :style="{ gridTemplateColumns: top20Grid }">
          <span class="t20h">#</span>
          <span class="t20h">Page</span>
          <span class="t20h t20h--sort" :class="{ 't20h--active': sortKey === 'date' }" @click="onSort('date')">
            Date<i class="material-icons t20-sort-icon">{{ sortIcon('date') }}</i>
          </span>
          <span class="t20h">Type</span>
          <span class="t20h">Publication</span>
          <span class="t20h t20h--sort t20h--right" :class="{ 't20h--active': sortKey === 'impressions' }" @click="onSort('impressions')">
            Imp.<i class="material-icons t20-sort-icon">{{ sortIcon('impressions') }}</i>
          </span>
          <span class="t20h t20h--sort t20h--right" :class="{ 't20h--active': sortKey === 'reactions' }" @click="onSort('reactions')">
            Réact.<i class="material-icons t20-sort-icon">{{ sortIcon('reactions') }}</i>
          </span>
          <span class="t20h t20h--sort t20h--right" :class="{ 't20h--active': sortKey === 'comments' }" @click="onSort('comments')">
            Comm.<i class="material-icons t20-sort-icon">{{ sortIcon('comments') }}</i>
          </span>
          <span class="t20h t20h--sort t20h--right" :class="{ 't20h--active': sortKey === 'shares' }" @click="onSort('shares')">
            Part.<i class="material-icons t20-sort-icon">{{ sortIcon('shares') }}</i>
          </span>
          <span class="t20h t20h--sort t20h--right" :class="{ 't20h--active': sortKey === 'engagement' }" @click="onSort('engagement')">
            Eng.<i class="material-icons t20-sort-icon">{{ sortIcon('engagement') }}</i>
          </span>
          <span class="t20h t20h--sort t20h--right" :class="{ 't20h--active': sortKey === 'rate' }" @click="onSort('rate')">
            Taux%<i class="material-icons t20-sort-icon">{{ sortIcon('rate') }}</i>
          </span>
          <span class="t20h"></span>
        </div>
        <div class="top20-table__row" v-for="(post, i) in top20Posts" :key="i" :style="{ gridTemplateColumns: top20Grid }">
          <span class="t20-rank">#{{ i + 1 }}</span>
          <div class="t20-page">
            <div class="t20-av-wrap">
              <img v-if="post._page.avatar" :src="post._page.avatar" class="t20-av" alt="">
              <span v-else class="t20-av-fallback">{{ (post._page.name || '?')[0].toUpperCase() }}</span>
              <img v-if="networkIcon(post._page.network)" :src="networkIcon(post._page.network)" class="t20-net-badge" alt="">
            </div>
            <span class="t20-page-name">{{ post._page.name }}</span>
          </div>
          <span class="t20-date">{{ fmtDate(post.created_time) }}</span>
          <span v-if="post.type || post.media_type" class="t20-type-badge" :class="typeBadgeClass(post.type || post.media_type)">
            {{ typeLabel(post.type || post.media_type) }}
          </span>
          <span v-else></span>
          <div class="t20-pub">
            <div class="t20-thumb">
              <img v-if="postImageUrl(post)" :src="postImageUrl(post)" class="t20-thumb-img" alt="">
              <i v-else class="material-icons t20-thumb-ph">image</i>
            </div>
            <span class="t20-msg">{{ truncate(post.message || post.video_description || post.content || '', 70) }}</span>
          </div>
          <span class="t20-num" :class="{ 't20-num--active': sortKey === 'impressions' }">{{ fmtNum(post.post_impressions || post.view_count || 0) }}</span>
          <span class="t20-num" :class="{ 't20-num--active': sortKey === 'reactions' }">{{ fmtNum(post.post_reactions_sum || post.like_count || 0) }}</span>
          <span class="t20-num" :class="{ 't20-num--active': sortKey === 'comments' }">{{ fmtNum(post.comments_count || 0) }}</span>
          <span class="t20-num" :class="{ 't20-num--active': sortKey === 'shares' }">{{ fmtNum(post.shares_count || 0) }}</span>
          <span class="t20-num" :class="{ 't20-num--active': sortKey === 'engagement' }">{{ fmtNum(postEngTotal(post)) }}</span>
          <span class="t20-num" :class="{ 't20-num--active': sortKey === 'rate' }">{{ postEngRate(post) }}</span>
          <a v-if="post.permalink_url" :href="post.permalink_url" target="_blank" rel="noopener" class="t20-link">
            <i class="material-icons">open_in_new</i>
          </a>
          <span v-else></span>
        </div>
      </div>
    </div>

    <!-- Démographie & Géographie agrégées -->
    <ReportingDemographics
      v-if="aggregatedDemographics"
      :stats="aggregatedDemographics"
      :page="null"
    />

  </div>
</template>

<script>
import MetricCard from './panels/MetricCard.vue'
import Graph from './Graph.vue'
import ReportingDemographics from './ReportingDemographics.vue'
import moment from 'moment'

var NETWORK_COLORS = {
  facebook:  '#1877F2',
  instagram: '#C13584',
  linkedin:  '#0077B5',
  tiktok:    '#010101',
  twitter:   '#1DA1F2',
  threads:   '#6B1F4D',
  youtube:   '#FF0000',
  pinterest: '#E60023',
}
function networkColor(net) { return NETWORK_COLORS[net] || '#6B1F4D' }

// Sum a scalar field across pages (prev = use previous_period)
// Returns null when usePrev=true and not ALL pages have previous_period loaded yet
function sumScalar(items, field, usePrev) {
  if (usePrev) {
    var allLoaded = items.every(function(item) {
      var p = item.stats && item.stats.page
      return p && p.previous_period != null
    })
    if (!allLoaded) return null
    return items.reduce(function(s, item) {
      var p = item.stats && item.stats.page
      if (!p || !p.previous_period) return s
      return s + (p.previous_period[field] || 0)
    }, 0)
  }
  return items.reduce(function(s, item) {
    var p = item.stats && item.stats.page
    if (!p) return s
    return s + (p[field] || 0)
  }, 0)
}

// Sum a time-series array field across pages into a scalar
function sumSeriesScalar(items, fields) {
  return items.reduce(function(s, item) {
    var p = item.stats && item.stats.page
    if (!p) return s
    for (var i = 0; i < fields.length; i++) {
      var arr = p[fields[i]]
      if (Array.isArray(arr) && arr.length) {
        return s + arr.reduce(function(a, d) { return a + (d.total || 0) }, 0)
      }
    }
    return s
  }, 0)
}

// Aggregate one field across all items into a date→value map
function aggregateDateMap(items, fields) {
  var map = {}
  items.forEach(function(item) {
    var p = item.stats && item.stats.page
    if (!p) return
    for (var i = 0; i < fields.length; i++) {
      var arr = p[fields[i]]
      if (Array.isArray(arr) && arr.length) {
        arr.forEach(function(d) {
          map[d.date] = (map[d.date] || 0) + (d.total || 0)
        })
        break
      }
    }
  })
  return map
}

// Build a chart with multiple datasets (each from a different field group, aggregated across all pages)
// metrics = [{fields, label, color, type}]
function buildMultiChart(items, metrics) {
  // Union of all dates across all metrics
  var allDates = {}
  metrics.forEach(function(m) {
    var map = aggregateDateMap(items, m.fields)
    Object.keys(map).forEach(function(d) { allDates[d] = true })
  })
  var dates = Object.keys(allDates).map(Number).sort(function(a, b) { return a - b })
  if (!dates.length) return { labels: [], data: [] }

  var labels = dates.map(function(d) { return moment(d * 1000).format('MM/DD/YYYY') })
  var datasets = []
  metrics.forEach(function(m) {
    var map = aggregateDateMap(items, m.fields)
    var values = dates.map(function(d) { return map[d] || 0 })
    if (!values.some(function(v) { return v > 0 })) return
    datasets.push({ label: m.label, data: values, type: m.type || 'line', color: m.color })
  })
  if (!datasets.length) return { labels: [], data: [] }
  return { labels: labels, data: datasets }
}

// Build engagement from posts array (fallback for FB/IG that have no daily series)
function buildEngagementFromPosts(items) {
  var dateMap = {}
  items.forEach(function(item) {
    var posts = (item.stats && item.stats.posts) || (item.stats && item.stats.page && item.stats.page.posts) || []
    posts.forEach(function(post) {
      if (!post.created_time) return
      var ts = Math.floor(new Date(post.created_time.substring(0, 10)).getTime() / 1000)
      if (!dateMap[ts]) dateMap[ts] = { r: 0, c: 0, s: 0 }
      dateMap[ts].r += post.post_reactions_sum || post.like_count || 0
      dateMap[ts].c += post.comments_count || 0
      dateMap[ts].s += post.shares_count || 0
    })
  })
  var dates = Object.keys(dateMap).map(Number).sort(function(a, b) { return a - b })
  if (!dates.length) return { labels: [], data: [] }
  var labels = dates.map(function(d) { return moment(d * 1000).format('MM/DD/YYYY') })
  var datasets = []
  var r = dates.map(function(d) { return dateMap[d].r })
  var c = dates.map(function(d) { return dateMap[d].c })
  var s = dates.map(function(d) { return dateMap[d].s })
  if (r.some(function(v) { return v > 0 })) datasets.push({ label: 'Réactions', data: r, type: 'bar', color: '#F56565' })
  if (c.some(function(v) { return v > 0 })) datasets.push({ label: 'Commentaires', data: c, type: 'bar', color: '#4299E1' })
  if (s.some(function(v) { return v > 0 })) datasets.push({ label: 'Partages', data: s, type: 'bar', color: '#68D391' })
  return { labels: labels, data: datasets }
}

// Build one line per network group for a time-series field
// Also falls back to post-based aggregation per network for engagement
function buildNetworkChart(networkGroups, fields, usePosts) {
  var allDates = {}
  networkGroups.forEach(function(group) {
    var map = aggregateDateMap(group.items, fields)
    Object.keys(map).forEach(function(d) { allDates[d] = true })
    if (usePosts) {
      group.items.forEach(function(item) {
        var posts = (item.stats && item.stats.posts) || (item.stats && item.stats.page && item.stats.page.posts) || []
        posts.forEach(function(post) {
          if (post.created_time) {
            var ts = Math.floor(new Date(post.created_time.substring(0, 10)).getTime() / 1000)
            allDates[ts] = true
          }
        })
      })
    }
  })
  var dates = Object.keys(allDates).map(Number).sort(function(a, b) { return a - b })
  if (!dates.length) return { labels: [], data: [] }
  var labels = dates.map(function(d) { return moment(d * 1000).format('MM/DD/YYYY') })

  var datasets = []
  networkGroups.forEach(function(group) {
    var map = aggregateDateMap(group.items, fields)
    // Post-based fallback
    if (usePosts && !Object.keys(map).length) {
      group.items.forEach(function(item) {
        var posts = (item.stats && item.stats.posts) || (item.stats && item.stats.page && item.stats.page.posts) || []
        posts.forEach(function(post) {
          if (!post.created_time) return
          var ts = Math.floor(new Date(post.created_time.substring(0, 10)).getTime() / 1000)
          var val = post.post_reactions_sum || post.like_count || 0
          val += post.comments_count || 0
          val += post.shares_count || 0
          map[ts] = (map[ts] || 0) + val
        })
      })
    }
    var values = dates.map(function(d) { return map[d] || 0 })
    if (!values.some(function(v) { return v > 0 })) return
    datasets.push({ label: group.network, data: values, type: 'line', color: networkColor(group.network) })
  })
  if (!datasets.length) return { labels: [], data: [] }
  return { labels: labels, data: datasets }
}

export default {
  name: 'ReportingComparisonAggregate',
  components: { MetricCard, Graph, ReportingDemographics },
  props: {
    pagesData: { type: Array, required: true },
    prevLoading: { type: Boolean, default: false },
    sortCriterion: { type: String, default: 'engagement' },
  },
  data() {
    return {
      resolvedImages: {},
      sortKey: 'engagement',
      sortDir: 'desc',
    }
  },
  watch: {
    top20Posts: {
      immediate: true,
      handler(posts) {
        if (posts && posts.length) this._fetchPostImages(posts)
      },
    },
    sortKey(val) {
      var pdf = { impressions: 'impressions', reactions: 'reactions', comments: 'comments', shares: 'shares', engagement: 'engagement', rate: 'rate' }
      this.$emit('update:sortCriterion', pdf[val] || 'engagement')
    },
  },
  methods: {
    postEngTotal(post) {
      return (post.post_reactions_sum || post.like_count || 0)
        + (post.comments_count || 0)
        + (post.shares_count || 0)
    },
    postEngRate(post) {
      if (post.reach_engagement != null) return parseFloat(post.reach_engagement).toFixed(2) + '%'
      var imp = post.post_impressions || post.view_count || 0
      var eng = this.postEngTotal(post)
      return imp > 0 ? (eng / imp * 100).toFixed(2) + '%' : '—'
    },
    onSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return 'unfold_more'
      return this.sortDir === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    postImageUrl(post) {
      var key = (post._page.id || '') + ':' + (post.post_id || post.id || '')
      return this.resolvedImages[key]
        || post.attachment_url
        || post.full_picture
        || post.thumbnail_url
        || post.media_url
        || post.cover_image_url
        || null
    },
    async _fetchPostImages(posts) {
      var BATCH = 5
      var { VUE_APP_API_HOST } = process.env
      var token = localStorage.getItem('token')
      var vm = this
      var needFetch = posts.filter(function(post) {
        var net = post._page.network
        if (!['facebook', 'instagram', 'tiktok'].includes(net)) return false
        if (post.attachment_url || post.full_picture || post.thumbnail_url || post.media_url || post.cover_image_url) return false
        var key = (post._page.id || '') + ':' + (post.post_id || post.id || '')
        return !vm.resolvedImages[key]
      })
      for (var i = 0; i < needFetch.length; i += BATCH) {
        await Promise.all(needFetch.slice(i, i + BATCH).map(async function(post) {
          var postId = post.post_id || post.id
          if (!postId) return
          var key = (post._page.id || '') + ':' + postId
          try {
            if (post._page.network === 'tiktok') {
              var url = post.cover_image_url || null
              if (url) vm.$set(vm.resolvedImages, key, url)
              return
            }
            var res = await fetch(VUE_APP_API_HOST + '/moderation/post?page=' + post._page.id + '&post=' + postId + '&inbox=true', {
              headers: { Authorization: token ? 'Bearer ' + token : '' },
            })
            if (!res.ok) return
            var data = await res.json()
            if (!data || !data.post) return
            var imgUrl = post._page.network === 'facebook'
              ? data.post.full_picture
              : (data.post.thumbnail_url || data.post.media_url)
            if (imgUrl) vm.$set(vm.resolvedImages, key, imgUrl)
          } catch (e) { /* ignore */ }
        }))
      }
    },
    networkIcon(network) {
      var map = {
        facebook:  require('@/assets/img/iconsNetwork/ico-facebook.png'),
        instagram: require('@/assets/img/iconsNetwork/ico-instagram.png'),
        linkedin:  require('@/assets/img/iconsNetwork/ico-linkedin.png'),
        tiktok:    require('@/assets/img/iconsNetwork/ico-tiktok.png'),
      }
      return map[network] || null
    },
    fmtDate(dt) {
      return dt ? moment(dt).format('DD/MM/YY') : ''
    },
    fmtNum(n) {
      if (!n) return '0'
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return n.toLocaleString()
    },
    truncate(s, max) {
      if (!s) return ''
      return s.length > max ? s.slice(0, max) + '…' : s
    },
    typeLabel(type) {
      var map = {
        photo: 'Image', image: 'Image', IMAGE: 'Image',
        video: 'Vidéo', VIDEO: 'Vidéo', reel: 'Reel', REEL: 'Reel',
        snippet: 'Lien', link: 'Lien',
        text: 'Texte', text_only: 'Texte', status: 'Texte',
        Story: 'Story', story: 'Story',
        carrousel: 'Carrousel', CAROUSEL_ALBUM: 'Carrousel', 'MULTI-IMAGE': 'Carrousel',
      }
      return map[type] || type || '—'
    },
    typeBadgeClass(type) {
      if (!type) return 'tpbadge-default'
      var t = type.toLowerCase()
      if (t === 'photo' || t === 'image') return 'tpbadge-image'
      if (t === 'video' || t === 'reel')  return 'tpbadge-video'
      if (t === 'snippet' || t === 'link') return 'tpbadge-link'
      if (t === 'story')  return 'tpbadge-story'
      if (t === 'carrousel' || t === 'multi-image' || t === 'carousel_album') return 'tpbadge-carousel'
      return 'tpbadge-default'
    },
  },

  computed: {
    networkGroups() {
      var groups = {}
      this.pagesData.forEach(function(item) {
        var net = item.page.network || 'other'
        if (!groups[net]) groups[net] = { network: net, items: [] }
        groups[net].items.push(item)
      })
      return Object.values(groups)
    },
    hasMultipleNetworks() { return this.networkGroups.length > 1 },
    networksLabel() {
      return this.networkGroups.map(function(g) { return g.network }).join(' · ')
    },
    totals() {
      var items = this.pagesData
      return {
        page_fans:             sumScalar(items, 'page_fans'),
        nb_posts:              sumScalar(items, 'nb_posts'),
        totalImpressions:      sumScalar(items, 'totalImpressions'),
        totalEngagement:       sumScalar(items, 'totalEngagement'),
        totalReactions:        sumScalar(items, 'totalReactions'),
        totalComments:         sumScalar(items, 'totalComments'),
        totalShares:           sumScalar(items, 'totalShares'),
        totalClicks:           sumScalar(items, 'totalClicks'),
        totalUniqueImpressions:sumScalar(items, 'totalUniqueImpressions'),
        organicFollowerGain:   sumScalar(items, 'organicFollowerGain'),
        paidFollowerGain:      sumScalar(items, 'paidFollowerGain'),
        totalReach:            sumSeriesScalar(items, ['totalReach']),
        totalOrganic:          sumSeriesScalar(items, ['page_impressions_organic_v2', 'page_impressions_organic']),
        totalPaid:             sumSeriesScalar(items, ['page_impressions_paid']),
        totalViral:            sumSeriesScalar(items, ['page_impressions_viral']),
        totalPageViews:        sumSeriesScalar(items, ['page_views_total', 'page_views_graph']),
        sumFanAdds:            sumSeriesScalar(items, ['page_fan_adds_unique', 'page_fan_adds']),
        sumFanRemoves:         sumSeriesScalar(items, ['page_fan_removes_unique', 'page_fan_removes']),
      }
    },
    prev() {
      var items = this.pagesData
      return {
        page_fans:        sumScalar(items, 'page_fans',        true),
        nb_posts:         sumScalar(items, 'nb_posts',         true),
        totalImpressions: sumScalar(items, 'totalImpressions', true),
        totalEngagement:  sumScalar(items, 'totalEngagement',  true),
        totalReactions:   sumScalar(items, 'totalReactions',   true),
        totalComments:    sumScalar(items, 'totalComments',    true),
        totalShares:      sumScalar(items, 'totalShares',      true),
        totalClicks:      sumScalar(items, 'totalClicks',      true),
      }
    },
    globalEngagementRate() {
      var eng = this.totals.totalEngagement, imp = this.totals.totalImpressions
      return imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : 0
    },
    prevEngagementRate() {
      var eng = this.prev.totalEngagement, imp = this.prev.totalImpressions
      return imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : null
    },

    // --- Sections ---
    fansSection() {
      var chart = buildMultiChart(this.pagesData, [
        { fields: ['dailyPageFans_graph', 'page_follows'], label: this.$t('dash.reporting.followers'), color: '#6B1F4D', type: 'line' },
        { fields: ['page_fan_adds_unique', 'page_fan_adds'], label: this.$t('dash.reporting.new_followers'), color: '#10B981', type: 'bar' },
        { fields: ['page_fan_removes_unique', 'page_fan_removes'], label: this.$t('dash.reporting.nb_fans.lost_fans'), color: '#EF4444', type: 'bar' },
        { fields: ['page_daily_follows_unique'], label: this.$t('dash.reporting.organicFollowerGain'), color: '#4299E1', type: 'bar' },
      ])
      return { show: this.totals.page_fans > 0 || chart.data.length > 0, chart: chart }
    },
    impressionsSection() {
      var chart = buildMultiChart(this.pagesData, [
        { fields: ['impression_posts_graph'],                                       label: this.$t('dash.reporting.impression'),                  color: '#6B1F4D', type: 'line' },
        { fields: ['page_impressions_organic_v2', 'page_impressions_organic'],      label: this.$t('dash.label.fb.impressions_organic'),          color: '#10B981', type: 'line' },
        { fields: ['page_impressions_paid'],                                        label: this.$t('dash.label.fb.impressions_paid'),             color: '#F59E0B', type: 'line' },
        { fields: ['page_impressions_viral'],                                       label: this.$t('dash.label.fb.impressions_viral'),            color: '#EF4444', type: 'line' },
        { fields: ['totalReach'],                                                   label: this.$t('dash.label.fb.impressions_reach'),            color: '#4299E1', type: 'line' },
        { fields: ['page_views_total', 'page_views_graph'],                         label: this.$t('dash.reporting.page_views'),                  color: '#8B5CF6', type: 'line' },
        { fields: ['page_unique_impressions_graph'],                                label: this.$t('dash.reporting.unique_impression'),           color: '#A855F7', type: 'line' },
      ])
      return { show: this.totals.totalImpressions > 0 || chart.data.length > 0, chart: chart }
    },
    engagementSection() {
      // Try daily series first
      var chart = buildMultiChart(this.pagesData, [
        { fields: ['reactionsGraph'],     label: this.$t('dash.reporting.reaction'), color: '#F56565', type: 'bar' },
        { fields: ['commentsGraph'],      label: this.$t('dash.reporting.comment'),  color: '#4299E1', type: 'bar' },
        { fields: ['sharesGraph'],        label: this.$t('dash.reporting.share'),    color: '#68D391', type: 'bar' },
        { fields: ['link_clicks_graph'],  label: this.$t('dash.reporting.clic'),     color: '#F59E0B', type: 'line' },
      ])
      // If no daily series, fall back to per-post aggregation
      if (!chart.data.length) chart = buildEngagementFromPosts(this.pagesData)
      return { show: this.totals.totalEngagement > 0 || chart.data.length > 0, chart: chart }
    },

    // --- Démographie agrégée ---
    aggregatedDemographics() {
      var items = this.pagesData

      // 1. Genre — normalise tous les formats en {name: 'M'|'F'|'U', value: N}
      var genderMap = {}
      items.forEach(function(item) {
        var p = item.stats && item.stats.page
        if (!p) return
        var raw = p.audience_gender || p.audience_by_gender || p.page_follows_by_gender
        if (Array.isArray(raw) && raw.length) {
          raw.forEach(function(e) {
            var k = (e.name === 'male' || e.name === 'M') ? 'M'
                  : (e.name === 'female' || e.name === 'F') ? 'F' : 'U'
            genderMap[k] = (genderMap[k] || 0) + (e.value || 0)
          })
        } else if (p.page_fans_gender_age && typeof p.page_fans_gender_age === 'object') {
          Object.keys(p.page_fans_gender_age).forEach(function(k) {
            if (k.startsWith('M.'))      genderMap.M = (genderMap.M || 0) + (p.page_fans_gender_age[k] || 0)
            else if (k.startsWith('F.')) genderMap.F = (genderMap.F || 0) + (p.page_fans_gender_age[k] || 0)
          })
        }
      })
      var audienceGender = ['M', 'F', 'U']
        .filter(function(k) { return (genderMap[k] || 0) > 0 })
        .map(function(k) { return { name: k, value: genderMap[k] } })

      // 2. Âge — somme par tranche
      var ageMap = {}
      items.forEach(function(item) {
        var p = item.stats && item.stats.page
        if (!p) return
        var raw = p.audience_gender_age || p.page_follows_gender_age || p.fans_gender_age
        if (!Array.isArray(raw)) return
        raw.forEach(function(e) {
          var name = e.name && e.name.includes('.') ? e.name.split('.')[1] : e.name
          if (!name) return
          ageMap[name] = (ageMap[name] || 0) + (e.value || 0)
        })
      })
      var audienceAge = Object.keys(ageMap)
        .filter(function(k) { return ageMap[k] > 0 })
        .map(function(k) { return { name: k, value: ageMap[k] } })

      // 3. Pays — moyenne pondérée par nb abonnés, résultat en %
      var countryMap = {}
      var totalWeight = 0
      items.forEach(function(item) {
        var p = item.stats && item.stats.page
        if (!p) return
        var raw = p.page_follows_country_percent || p.page_fans_country
                || p.audience_country_percent   || p.audience_country
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
        var w = p.page_fans || 1
        totalWeight += w
        Object.keys(raw).forEach(function(code) {
          countryMap[code] = (countryMap[code] || 0) + (parseFloat(raw[code]) || 0) * w
        })
      })
      var countryObj = null
      if (totalWeight > 0 && Object.keys(countryMap).length) {
        countryObj = {}
        Object.keys(countryMap).forEach(function(code) {
          countryObj[code] = countryMap[code] / totalWeight
        })
      }

      // 4. Villes — somme des valeurs absolues
      var cityMap = {}
      items.forEach(function(item) {
        var p = item.stats && item.stats.page
        if (!p) return
        var raw = p.page_fans_city || p.audience_city
        if (!Array.isArray(raw)) return
        raw.forEach(function(e) {
          if (!e.name) return
          cityMap[e.name] = (cityMap[e.name] || 0) + (e.value || 0)
        })
      })
      var audienceCity = Object.keys(cityMap)
        .filter(function(k) { return cityMap[k] > 0 })
        .map(function(k) { return { name: k, value: cityMap[k] } })

      var hasAny = audienceGender.length || audienceAge.length || countryObj || audienceCity.length
      if (!hasAny) return null

      return {
        page: {
          audience_gender:     audienceGender.length ? audienceGender : undefined,
          audience_gender_age: audienceAge.length    ? audienceAge    : undefined,
          audience_country:    countryObj            || undefined,
          audience_city:       audienceCity.length   ? audienceCity   : undefined,
        }
      }
    },

    top20Grid() {
      return '26px 130px 52px 66px 1fr 72px 70px 70px 64px 70px 68px 24px'
    },

    // --- Top 20 posts ---
    allPostsForTop() {
      var all = []
      this.pagesData.forEach(function(item) {
        var posts = (item.stats && item.stats.posts) || []
        posts.forEach(function(post) {
          if (post.type === 'Story' || post.story || (post.media_type || '').toLowerCase().includes('story')) return
          all.push(Object.assign({}, post, { _page: item.page }))
        })
      })
      return all
    },
    top20Posts() {
      var vm = this
      var key = this.sortKey
      var dir = this.sortDir === 'asc' ? 1 : -1
      var sorted = this.allPostsForTop.slice().sort(function(a, b) {
        var va, vb
        if (key === 'date')        { va = new Date(a.created_time || 0); vb = new Date(b.created_time || 0) }
        else if (key === 'impressions') { va = a.post_impressions || a.view_count || 0; vb = b.post_impressions || b.view_count || 0 }
        else if (key === 'reactions')   { va = a.post_reactions_sum || a.like_count || 0; vb = b.post_reactions_sum || b.like_count || 0 }
        else if (key === 'comments')    { va = a.comments_count || 0; vb = b.comments_count || 0 }
        else if (key === 'shares')      { va = a.shares_count || 0; vb = b.shares_count || 0 }
        else if (key === 'rate') {
          var ia = a.post_impressions || a.view_count || 0
          var ib = b.post_impressions || b.view_count || 0
          va = a.reach_engagement != null ? parseFloat(a.reach_engagement) : (ia > 0 ? vm.postEngTotal(a) / ia * 100 : 0)
          vb = b.reach_engagement != null ? parseFloat(b.reach_engagement) : (ib > 0 ? vm.postEngTotal(b) / ib * 100 : 0)
        }
        else { va = vm.postEngTotal(a); vb = vm.postEngTotal(b) }
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
      return sorted.slice(0, 20)
    },

    // --- Par réseau ---
    networkImpressionsChart() {
      return buildNetworkChart(this.networkGroups, ['impression_posts_graph'], false)
    },
    networkEngagementChart() {
      return buildNetworkChart(this.networkGroups, ['reactionsGraph', 'engagement_posts_graph'], true)
    },
    networkFansChart() {
      return buildNetworkChart(this.networkGroups, ['dailyPageFans_graph', 'page_follows', 'page_daily_follows_unique'], false)
    },
  },
}
</script>

<style scoped>
.prev-loading-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #9ba3b7;
  background: #f9f7fb;
  border: 1px solid #f1edf5;
  padding: 2px 10px;
  border-radius: 20px;
}
.prev-loading-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #6B1F4D;
  animation: prev-pulse 1.2s ease-in-out infinite;
  flex-shrink: 0;
}
@keyframes prev-pulse {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50%       { opacity: 1;   transform: scale(1);   }
}
.agg-sections { display: flex; flex-direction: column; gap: 16px; }
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.agg-badge {
  margin-left: auto;
  font-size: 12px;
  color: #9ba3b7;
  background: #f1edf5;
  padding: 2px 10px;
  border-radius: 20px;
  text-transform: capitalize;
}
.metric-row { display: flex; flex-wrap: wrap; gap: 14px; }

/* Top 20 table */
.top20-table { width: 100%; overflow-x: auto; }
.top20-table__head,
.top20-table__row {
  display: grid;
  gap: 6px;
  align-items: center;
  padding: 7px 4px;
}
.top20-table__head {
  border-bottom: 2px solid #f1edf5;
  padding-bottom: 9px;
}
.top20-table__row {
  border-bottom: 1px solid #f9f7fb;
  font-size: 12px;
  color: #121826;
  transition: background 0.1s;
}
.top20-table__row:last-child { border-bottom: none; }
.top20-table__row:hover { background: #faf8fc; border-radius: 6px; }

.t20h {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  font-weight: 600;
  color: #9ba3b7;
  letter-spacing: 0.2px;
  white-space: nowrap;
}
.t20h--sort { cursor: pointer; user-select: none; }
.t20h--sort:hover { color: #6B1F4D; }
.t20h--active { color: #6B1F4D; }
.t20h--right { justify-content: flex-end; }
.t20-sort-icon { font-size: 13px; }

.t20-rank { font-size: 11px; font-weight: 700; color: #c4afd6; text-align: right; }
.t20-page { display: flex; align-items: center; gap: 6px; min-width: 0; }
.t20-av-wrap { position: relative; width: 26px; height: 26px; flex-shrink: 0; }
.t20-av { width: 26px; height: 26px; border-radius: 50%; object-fit: cover; display: block; }
.t20-av-fallback {
  width: 26px; height: 26px; border-radius: 50%; background: #f1edf5; color: #6B1F4D;
  display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700;
}
.t20-net-badge {
  position: absolute; bottom: -2px; right: -3px;
  width: 11px; height: 11px; border-radius: 50%; border: 1.5px solid #fff; object-fit: cover;
}
.t20-page-name { font-size: 11px; font-weight: 600; color: #3d2a52; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.t20-date { font-size: 11px; color: #9ba3b7; }
.t20-type-badge {
  padding: 2px 6px; border-radius: 10px; font-size: 10px; font-weight: 600; letter-spacing: 0.2px; white-space: nowrap;
  justify-self: start;
}
.tpbadge-image   { background: #dcfce7; color: #166534; }
.tpbadge-video   { background: #dbeafe; color: #1e40af; }
.tpbadge-link    { background: #ffedd5; color: #9a3412; }
.tpbadge-story   { background: #f3e8ff; color: #6b21a8; }
.tpbadge-carousel{ background: #cffafe; color: #164e63; }
.tpbadge-default { background: #f1f5f9; color: #475569; }
.t20-pub { display: flex; align-items: center; gap: 8px; min-width: 0; }
.t20-thumb {
  width: 32px; height: 32px; border-radius: 5px; overflow: hidden; flex-shrink: 0;
  background: #f1edf5; display: flex; align-items: center; justify-content: center;
}
.t20-thumb-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.t20-thumb-ph { font-size: 15px; color: #c4afd6; }
.t20-msg { font-size: 12px; color: #555e75; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; min-width: 0; }
.t20-num { font-size: 12px; font-weight: 600; color: #555e75; text-align: right; }
.t20-num--active { color: #6B1F4D; }
.t20-link {
  display: flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 5px;
  color: #9ba3b7; text-decoration: none; transition: all 0.12s;
}
.t20-link:hover { background: #f1edf5; color: #6B1F4D; }
.t20-link .material-icons { font-size: 14px; }

.graph-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 16px 0 6px;
}
</style>
